import { KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, type OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from '@fino-ui/button';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoInputModule } from '@fino-ui/input';
import { BaseModalComponent } from '@fino-ui/modal';
import { TranslationModule } from '@fino-ui/translation';
import { compact, map } from 'lodash-es';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ARCHIVING_FLOW, type Archiving } from 'src/app/modules/kwh/kwh.model';
import { InfoComponent } from '../../../../../../components/info/info.component';
import { ArchivingService } from '../../archiving.service';
import { ARCHIVING_MAP, DOCUMENT_TYPES } from '../../kwh.archiving';

@Component({
  selector: 'app-archiving-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslationModule,
    InfoComponent,
    FormsModule,
    ReactiveFormsModule,
    FinoInputModule,
    FinoIconModule,
    ButtonModule,
    KeyValuePipe,
  ],
})
export class ArchivingSettings
  extends BaseModalComponent<
    {
      archiving: Archiving;
    },
    Archiving
  >
  implements OnInit
{
  constructor(
    protected archivingService: ArchivingService,
    private appService: AppService,
  ) {
    super();
    Object.assign(this, this.data ?? {});
  }
  archiving: Archiving;
  archivingDocTypes = DOCUMENT_TYPES;
  archivingForm = new SubmittableForm({});
  archivingSubscription: Subscription = Subscription.EMPTY;
  errorMessage: string;

  archivingPossible(transferringType: string, documentType: string) {
    return ARCHIVING_MAP[transferringType].includes(documentType);
  }

  abort() {
    this.close();
  }

  ngOnInit(): void {
    map(ARCHIVING_MAP, (docTypes: string[], transferringType: string) => {
      map(docTypes, (docType) => {
        const { docTypes } = this.archiving;
        const controlName = `${transferringType}_${docType}`;
        this.archivingForm.addControl(
          controlName,
          new FormControl(docTypes.includes(controlName)),
        );
      });
    });
  }

  get buttonLoading() {
    return !this.archivingSubscription.closed;
  }

  validateForm(docTypes: string[]) {
    let error = '';
    this.archivingForm.setErrors(null);
    if (docTypes.length === 0) {
      error = 'empty';
    }

    if (!error) {
      return;
    }
    this.archivingForm.setErrors({ [error]: true });
    this.errorMessage = `archiving-settings.error.${error}`;
  }

  get archivingFormInvalid() {
    return this.archivingForm.invalid;
  }

  submit() {
    const { value } = this.archivingForm;
    const docTypes = compact(map(value, (value, key) => (value ? key : null)));
    this.validateForm(docTypes);

    if (this.archivingForm.invalid) return;

    this.archivingSubscription = this.archivingService
      .submitArchivingChanges(docTypes)
      .subscribe({
        next: (resp) => {
          this.appService.success('kwh.actions.archiving.success');

          this.archiving.flow = ARCHIVING_FLOW.FINAL_STATE;
          this.archiving.docTypes = docTypes;

          this.result = this.archiving;
          this.close();
        },
        error: (resp) => {
          this.appService.error('kwh.actions.archiving.error');
        },
      });
  }
}
